'use client';

import React from 'react';
import Image from 'next/image';
import {useSkillzStyle} from '@/app/(app)/games/[gid]/_src/SkillzStyleProvider';
// Using official Skillz powered logo from the app

function ProgressBar({progress}: {progress: number}) {
  const {themeJSON: theme} = useSkillzStyle();
  const {viewPrimaryColor, progressBarColor} = theme.sheetStyles;
  const loadingBarBackground = viewPrimaryColor.backgroundColor;
  const loadingBarColor = progressBarColor.backgroundColor;

  return (
    <div className='w-full flex justify-center items-center flex-col'>
      <div
        className='w-1/2 h-5 bg-black dark:bg-white rounded-md flex justify-start pl-1 items-center'
        style={{
          backgroundColor: loadingBarBackground,
        }}
      >
        <div
          className='h-3 bg-white dark:bg-black rounded-sm transition-all duration-200 mr-1'
          style={{
            backgroundColor: loadingBarColor,
            width: `${Math.min(progress * 100, 100)}%`,
          }}
        ></div>
      </div>
    </div>
  );
}

function StaticLoader() {
  return (
    <div className='w-full flex justify-center items-center'>
      <span className='w-8 inline-flex justify-between items-center mx-6'>
        <div className='h-2 w-2 dark:bg-white bg-black rounded-full animate-[opacity_3s_ease-in-out_infinite] animation-delay-[1000ms]'></div>
        <div className='h-2 w-2 dark:bg-white bg-black rounded-full animate-[opacity_3s_ease-in-out_infinite] animation-delay-[1150ms]'></div>
        <div className='h-2 w-2 dark:bg-white bg-black rounded-full animate-[opacity_3s_ease-in-out_infinite] animation-delay-[1300ms]'></div>
      </span>
    </div>
  );
}

type Props = {
  progress?: number;
  withUI?: boolean;
  disableAnimation?: boolean;
  coverHeader?: boolean;
};

export default function LoadingScreen({progress, withUI, coverHeader}: Props) {
  const showProgressBar = typeof progress !== 'undefined';
  const containerClass = coverHeader
    ? 'fixed inset-0 z-50 overflow-hidden'
    : `w-screen ${withUI ? 'h-[calc(100dvh_-_80px)]' : 'h-screen'} relative overflow-hidden`;

  return (
    <div className={containerClass}>
      <video
        className='absolute inset-0 w-full h-full object-cover'
        autoPlay
        muted
        loop
        playsInline
      >
        <source src='/Arcade_Loop_Background_01.mp4' type='video/mp4' />
        <div className='absolute inset-0 bg-gradient-to-b from-blue-900 via-purple-900 to-blue-900'>
          <div className='absolute inset-0 opacity-20'>
            <div className='absolute bottom-0 left-0 w-full h-1/3 bg-gradient-to-t from-gray-800 to-transparent'></div>
            <div className='absolute bottom-0 left-1/4 w-16 h-32 bg-gray-700 rounded-t-lg opacity-30'></div>
            <div className='absolute bottom-0 left-1/2 w-16 h-32 bg-gray-700 rounded-t-lg opacity-30'></div>
            <div className='absolute bottom-0 right-1/4 w-16 h-32 bg-gray-700 rounded-t-lg opacity-30'></div>
          </div>
        </div>
      </video>

      <div className='absolute inset-0 bg-black/50'></div>

      <div className='relative z-10 w-full h-full flex flex-col justify-between items-center py-8'>
        <div className='flex flex-col items-center mt-8 gap-1'>
          <Image
            src='/skillz-powered-logo-white.svg'
            className='h-16 w-auto'
            alt='Skillz'
            width={111}
            height={70}
            priority={true}
          />
          <div className='w-2.5 h-px bg-white opacity-50 mt-8 mb-8' />
          <Image
            src='/playz-logo-white.svg'
            alt='Playz'
            width={60}
            height={38}
            className='w-[60px] h-[38px] sm:w-[72px] sm:h-[45px] md:w-[84px] md:h-[53px] object-contain'
            priority={true}
          />
        </div>

        <div className='flex-1 flex flex-col items-center justify-center'>
          <div className='mb-6'>
            <Image
              src='/ic_fair_play_shield_skillz_app.png'
              alt='Fair Play Shield'
              width={130}
              height={130}
              className='w-32 h-32 object-contain'
              priority={true}
            />
          </div>
          <div
            className='text-center text-white max-w-sm'
            style={{
              fontFamily: 'OpenSans-Regular, sans-serif',
              fontSize: '17px',
              lineHeight: '1.29',
              whiteSpace: 'pre-line',
            }}
          >
            Match with{' '}
            <span style={{fontFamily: 'OpenSans-Bold, sans-serif', fontWeight: 'bold'}}>
              real players
            </span>{' '}
            of equal skill{'\n'}
            Win{' '}
            <span style={{fontFamily: 'OpenSans-Bold, sans-serif', fontWeight: 'bold'}}>
              real cash prizes
            </span>{' '}
            with easy withdrawals{'\n'}
            Compete for real,{' '}
            <span style={{fontFamily: 'OpenSans-Bold, sans-serif', fontWeight: 'bold'}}>
              no bots
            </span>{' '}
            guaranteed!
          </div>
        </div>
        <div className='w-full pb-8'>
          {showProgressBar ? <ProgressBar progress={progress} /> : <StaticLoader />}
        </div>
      </div>
    </div>
  );
}
